/**
 * near_lod — which LOD the NEAR foliage ring and settlement props load
 * (settlement study R2, 2026-09-02). Pure over the preset so the harness pins it.
 *
 * Before: the near ring and every prop took the canonical LOD0 at EVERY tier
 * (a 10 MB shrub, a 5 MB lamppost) while the far ring took LOD1. Now the tier
 * decides: potato/low/med load the LOD1 variant up close (half the triangles,
 * 512² textures — foliage is the frame's triangle whale), high/ultra keep
 * LOD0. A saved preset from before this field simply inherits its base tier's
 * default (loadSettings overlays onto a clean preset).
 */
import type { TierPreset } from './quality_tiers.js';

/**
 * LOD for foliage scattered in the player's own chunk ring.
 *
 * LOD2 (2026-09-15). This was capped at 1 — half the triangles — which is not
 * enough when the cooked LOD0 is itself oversized: a live frame was carrying
 * 2.97 M triangles of foliage over 151 instances, ~19.6 k each, because the
 * tree sources were ingested with decimation disabled and even LOD2 of an
 * undecimated 150 k tree is ~22 k. The cooker now gives foliage a real budget
 * (tools/asset-pipeline/ingest.mjs), but the cheap tiers should not be paying
 * LOD1 for near foliage either way — that is where the frame's triangle whale
 * has always lived. LOD2 is 15 %, so near foliage drops ~3.3× with today's
 * assets and ~14× once they are re-cooked.
 */
export function nearFoliageLod(preset: Pick<TierPreset, 'nearFoliageLod'>): 0 | 1 | 2 {
  const l = preset.nearFoliageLod;
  return l === 0 ? 0 : l === 2 ? 2 : 1;
}

/** LOD for props/decor placed by a settlement (structures keep their own rule). */
export function propLod(preset: Pick<TierPreset, 'propLod'>): 0 | 1 | 2 {
  // 2 = the 15 % LOD2 (real since the 2026-09-18 hinterland re-cook); loadRepresentation falls back to the nearest LOWER level a prop lacks.
  return preset.propLod === 0 ? 0 : preset.propLod === 2 ? 2 : 1;
}

/** Prefab keys that are props or decor (not structures, not foliage). */
export function isPropKey(prefabKey: string): boolean {
  return prefabKey.startsWith('prop:') || prefabKey.startsWith('decor:');
}
